import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Breadcrumb, Button, Checkbox, Icon, Loader } from "semantic-ui-react";
import BCMessage from "../ui/BCMessage";
import BCCardMenuBar from "../ui/BCCardMenuBar";
import LOKanban from "../ui/LOKanban";
import BCTable from "../ui/BCTable";
import BCModal from "../ui/BCModal";
import LODealForm from "./LODealForm";
import LOBorrowerForm from "./LOBorrowerForm";
import LOContactForm from "./LOContactForm";
import BCButton from "../ui/BCButton";
import { getDeals } from "../../api/LOApi";
import { getMockDeals } from "../../api/mock/MockLOApi";
import { convertToDollar } from "../../Helpers";
import "../../styles/deals.scss";
import { FeatureFlags } from "../constants/LocalServerConstants";

const LODeals = ({ 
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage,
  errorResponse, 
  setErrorResponse,
  dealData,
  setDealData, 
  isModalOpen, 
  setIsModalOpen 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isKanban, setIsKanban] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [newDealType, setNewDealType] = useState("default");
  const [newDeal, setNewDeal] = useState({ state: "WA" } as any);
  const [newBorrower, setNewBorrower] = useState({ state: "WA" } as any);
  const [newContact, setNewContact] = useState({ state: "WA" } as any);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleDealCard = useCallback((id) => {
    history.push(`/deal/${id}`);
  }, [history]);

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const filterDealsList = (deals) => {
    return deals.map((element) => {
      return {...element, cards: element.cards.filter((subElement) => subElement.name.toLowerCase().includes(searchValue.toLowerCase()))};
    });
  };

  const convertDealsToTableFormat = (deals) => {
    const result = [] as any;
    deals.forEach((dealType) => {
      dealType.cards.forEach((deal) => {
        result.push({
          "Deal Status": dealType.name,
          "Borrower": deal.name,
          "Address": deal.address,
          "Loan Amount": convertToDollar(deal.loanAmount),
          "Close Date": deal.closing
        });
      }); 
    });

    return result;
  };

  const handleModalClose = () => {
    setNewDealType("default");
    setIsModalOpen(false);
  };

  const handleNewDealCreate = () => {
    switch (newDealType) {
      case "borrower":
        FeatureFlags.IS_DEBUG && console.log("Create new deal borrower");
        FeatureFlags.IS_DEBUG && console.log(newBorrower);
        setNewDealType("default");
        break;
      case "contact":
        FeatureFlags.IS_DEBUG && console.log("Create new deal contact");
        FeatureFlags.IS_DEBUG && console.log(newContact);
        setNewDealType("default");
        break;
      default:
        FeatureFlags.IS_DEBUG && console.log("Create new deal");
        FeatureFlags.IS_DEBUG && console.log(newDeal);
        setIsModalOpen(false);
    }
  };

  useEffect(() => {
    const getLODealsAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockDeals() : await getDeals();
        if (response && response.ok) {
          setIsMessage(false);
          setDealData(await response.json());
        }
        else {
          FeatureFlags.IS_DEBUG && console.log(response);
          setIsMessage(true);
        }
      }
      catch (error) {
        FeatureFlags.IS_DEBUG && console.log(error);
        setErrorResponse(error);
        setIsMessage(false);
        handleError();
      }
      setIsLoading(false);
    };

    getLODealsAsync();
  }, [setIsMessage, handleError, setDealData]);

  const sortDealArray = (arr, property) => {
    arr.sort((a, b) => {
      return a[property] - b[property];
    });
    return arr;
  };

  const NewDealHeader = ({ type }) => {
    return (
      <Breadcrumb size={"huge"}>
        {type === "default" && <Breadcrumb.Section className="new-deal-breadcrumb" active>New Deal</Breadcrumb.Section>}
        {type !== "default" && <React.Fragment>
          <Breadcrumb.Section className="new-deal-breadcrumb" link onClick={() => setNewDealType("default")}>New Deal</Breadcrumb.Section>
          <Breadcrumb.Divider icon='right chevron' />
        </React.Fragment>}
        {type === "borrower" && <Breadcrumb.Section active>Borrower</Breadcrumb.Section>}
        {type === "contact" && <Breadcrumb.Section active>Contact</Breadcrumb.Section>}
      </Breadcrumb>
    );
  };

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader active inline="centered" size={"big"} /> : <div>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        <BCCardMenuBar 
          hasSearch 
          handleSearchChange={handleSearchChange} 
          hasViewFilter 
          leftCustom={<React.Fragment>
            {isGreaterThanMobile ? <BCButton labelPosition="left" iconName="add" handleOnClick={() => setIsModalOpen(true)}>
              <div>New Deal</div>
            </BCButton> :
            <BCButton handleOnClick={() => setIsModalOpen(true)}>
              <Icon className="add" fitted />
            </BCButton>}
            <Checkbox className="view-toggle" toggle onChange={() => setIsKanban(!isKanban)}/>
          </React.Fragment>}
        />
        {isKanban ? <LOKanban
          data={dealData ? filterDealsList(sortDealArray(dealData, "index")) : []}
          setData={setDealData}
          handleCard={handleDealCard}
        /> : <BCTable 
          data={convertDealsToTableFormat(dealData)}
          sortable={["Deal Status"]}
          checkbox
        />}
        <BCModal 
          isModalOpen={isModalOpen} 
          onClose={handleModalClose}
          onOpen={() => setIsModalOpen(true)}
          header={<NewDealHeader type={newDealType} />}
          content={newDealType === "borrower" ? <LOBorrowerForm newBorrower={newBorrower} setNewBorrower={setNewBorrower} /> : 
            newDealType === "contact" ? <LOContactForm newContact={newContact} setNewContact={setNewContact} /> : 
            <LODealForm
              deals={dealData}
              newDeal={newDeal}
              setNewDeal={setNewDeal}
              handleAddBorrower={() => setNewDealType("borrower")}
              handleAddContact={() => setNewDealType("contact")}
            />}
          actions={<Button type="submit" className="dashboard-button new-deal-button" fluid onClick={handleNewDealCreate}>
            <div>CREATE</div>
          </Button>}
        />
      </div>}
    </div>
  );
};

export default LODeals;